import React from 'react'
import _ from 'lodash'
import cn from 'classnames'
import { NextRouter, withRouter } from 'next/router'
import { connect, ConnectedProps } from 'react-redux'

import lang from 'lang'
import {
  addFieldsErrors,
  changeMoodMeterFormField,
  clearFieldError,
} from 'page-modules/mood-meter/service/actions'
import { validateFields } from 'common/validator'
import { AppState } from 'common/store/reducer'

import EmotionWord from './emotion-word'
import PlotValidations from './plot-validations'

interface EmotionWordData {
  id: string
  name: string
  definition: string
  pleasantness_lower: number
  pleasantness_higher: number
  energy_lower: number
  energy_higher: number
}

const distanceToRectangleCenter = (
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) => {
  const centerPoint = {
    x: (x2 + x1) / 2,
    y: (y2 + y1) / 2,
  }
  return Math.sqrt((centerPoint.x - px) ** 2 + (centerPoint.y - py) ** 2)
}

const pointRectangleDistance = (
  pX: number,
  pY: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) => {
  const minX = Math.min(x1, x2)
  const maxX = Math.max(x1, x2)
  const minY = Math.min(y1, y2)
  const maxY = Math.max(y1, y2)

  // only Y distanced
  if (pX >= minX && pX <= maxX) {
    if (pY > maxY) {
      return Math.abs(pY - maxY)
    }
    if (pY < minY) {
      return Math.abs(pY - minY)
    }
  }

  // only X distanced
  if (pY >= minY && pY <= maxY) {
    if (pX > maxX) {
      return Math.abs(pX - maxY)
    }
    if (pX < minX) {
      return Math.abs(pX - minX)
    }
  }

  if ((pX < minX || pX > maxY) && (pY < minY || pY > minY)) {
    // set a right triangle and use Pythagorean theorem
    const A = pX < minX ? minX - pX : pX - maxX
    const B = pY < minY ? minY - pY : pY - maxY
    return Math.sqrt(A ** 2 + B ** 2)
  }

  return 0
}

const connector = connect(
  (state: AppState) => ({
    requestError: state.moodMeter.request.postMoodMeterPlot.error.message,
    errors: state.moodMeter.forms.plot.errors,
    pleasantnessLevel: state.moodMeter.forms.plot.data.pleasantness,
    energyLevel: state.moodMeter.forms.plot.data.energy,
    reason: state.moodMeter.forms.plot.data.reason,
    chosenEmotionWord: state.moodMeter.forms.plot.data.chosenEmotionWord,
    emotionWords: state.emotionWords.items,
  }),
  (dispatch) => ({
    changeFormField: (key: string, value: string | number) =>
      dispatch(changeMoodMeterFormField(key, value)),
    addFieldsErrors: (errors: Record<string, string>) => dispatch(addFieldsErrors(errors)),
    clearFieldError: (field: string) => dispatch(clearFieldError(field)),
  }),
)

type Props = ConnectedProps<typeof connector> & {
  submitHandler: () => void
  router: NextRouter
}

interface State {
  showMoreEmotionWords: boolean
}

class EmotionSection extends React.Component<Props, State> {
  static validations = {
    reason: 'optional nonEmpty length160',
    chosenEmotionWord: 'nonEmpty',
  }

  state: State = {
    showMoreEmotionWords: false,
  }

  createFieldHandler(fieldName: string) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      if (this.props.changeFormField) {
        this.props.changeFormField(fieldName, e.target.value)
      }
    }
  }

  chooseWordHandler(emotionWordId: string) {
    return () => {
      this.props.clearFieldError('chosenEmotionWord')
      this.props.changeFormField('chosenEmotionWord', emotionWordId)
    }
  }

  createTextareaFieldHandler(fieldName: string) {
    return (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (this.props.changeFormField) {
        this.props.changeFormField(fieldName, e.target.value)
      }
    }
  }

  submitInternalHandler() {
    return () => {
      const fields = _.mapValues(_.pick(this.props, Object.keys(PlotValidations)), (value, key) => {
        return {
          value,
          validations: PlotValidations[key as keyof typeof PlotValidations],
        }
      })

      const errors = validateFields(fields)

      if (_.isEmpty(errors)) {
        this.props.submitHandler()
      } else {
        this.props.addFieldsErrors(errors as Record<string, string>)
      }
    }
  }

  componentDidMount() {
    this.props.changeFormField('chosenEmotionWord', '')
  }

  componentDidUpdate() {
    if (!_.some(this.availableEmotionWords, ['id', this.props.chosenEmotionWord])) {
      this.props.changeFormField('chosenEmotionWord', '')
    }
  }

  get availableEmotionWords() {
    // We need to have a proximity algorithm for rendering emotion words which are closer to the pleasantness and energy levels of the user
    // Let's define each emotion word as a rectangle with it's `*_lower` and `*_higher` coordinate.
    // Let's define pleasantness and energy level as a Point(px, py) on our plane
    // 1, We sort all emotion words by using "distance to collision". :comet:
    //    - by calculating the distance to the emotion rectangle from our Point.
    //    - We add to that distance the distance of the Point to the emotion rectangle center
    // 2. We take the first X results. We're using stable sort so they may be alphabetically sorted
    /*

      Rect A _________________  will be second result because Point is further away from it's center
            |Rect B__         | will be one of first results because point is closer to it's center
            |   |   *|        |
            |   |____|        |
            |                 |
            ------------------
      Rect C ______________  will be one of last results because Point is not inside it
            |             |
            |             |
            ---------------

    */
    // This way we will have only emotion words which are closer to the user energy and pleasantness levels
    // Stuff current algorithm is not taking in mind:
    //   - surface area of the rectangle, i.e. it's not considered more difficult for Point to
    // be in rectangle with smaller surface
    const { pleasantnessLevel, energyLevel, emotionWords } = this.props
    return _.take(
      _.sortBy(emotionWords as EmotionWordData[], (emotionWord) => {
        return (
          pointRectangleDistance(
            pleasantnessLevel,
            energyLevel,
            emotionWord.pleasantness_lower,
            emotionWord.energy_higher,
            emotionWord.pleasantness_higher,
            emotionWord.energy_lower,
          ) +
          distanceToRectangleCenter(
            pleasantnessLevel,
            energyLevel,
            emotionWord.pleasantness_lower,
            emotionWord.energy_higher,
            emotionWord.pleasantness_higher,
            emotionWord.energy_lower,
          )
        )
      }),
      this.state.showMoreEmotionWords ? 16 : 8,
    )
  }

  renderAvailableEmotionWords() {
    return _.map(this.availableEmotionWords, (word) => (
      <EmotionWord
        key={word.id}
        word={word}
        onClick={this.chooseWordHandler(word.id)}
        isChosen={this.props.chosenEmotionWord === word.id}
        disabled={this.disabled}
      />
    ))
  }

  renderReasonInput() {
    if (this.props.router.query.shortCode) {
      // A business requirement is not to ask for reason when doing in-group MM plot
      return null
    }
    return (
      <>
        <label htmlFor="reason">{lang.page.moodMeter.step.emotion.describeReason}</label>
        <span className={cn('form-error', { 'is-visible': this.props.errors.reason })}>
          {this.props.errors.reason}
        </span>
        <textarea
          disabled={this.disabled}
          id="reason"
          value={this.props.reason}
          onChange={this.createTextareaFieldHandler('reason')}
        />
      </>
    )
  }

  get disabled() {
    return this.props.energyLevel === 0 && this.props.pleasantnessLevel === 0
  }

  render() {
    return (
      <>
        <div className="mm-words-list">
          <form action="">
            <fieldset>
              <legend>{lang.page.moodMeter.step.emotion.selectEmotionWord}</legend>
              <span
                className={cn('form-error', {
                  'is-visible': this.props.errors.chosenEmotionWord,
                })}>
                {lang.page.moodMeter.step.emotion.selectEmotionWordErr}
              </span>
              <div className="mm-words-list-wrapper">{this.renderAvailableEmotionWords()}</div>
              <p>
                <button
                  onClick={() =>
                    this.setState((prevState) => ({
                      showMoreEmotionWords: !prevState.showMoreEmotionWords,
                    }))
                  }
                  disabled={this.disabled}
                  type="button"
                  className={cn('button clear more', {
                    less: this.state.showMoreEmotionWords,
                  })}>
                  {this.state.showMoreEmotionWords
                    ? lang.page.moodMeter.step.emotion.showLessEmotionWords
                    : lang.page.moodMeter.step.emotion.showMoreEmotionWords}
                  <span aria-hidden="true">
                    <i className="icon-down-chevron" />
                  </span>
                </button>
              </p>
            </fieldset>
            {this.renderReasonInput()}
            <button
              className="button mm-next float-right"
              type="button"
              disabled={this.disabled}
              onClick={this.submitInternalHandler()}>
              {lang.saveButton}
            </button>
            <span className={cn('form-error', { 'is-visible': this.props.requestError })}>
              {this.props.requestError}
            </span>
          </form>
        </div>
      </>
    )
  }
}

const ConnectedEmotionSection = connector(EmotionSection)
export default withRouter(ConnectedEmotionSection) as React.ComponentType<{
  submitHandler: () => void
}>
